import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import {
    dismissPushPermissionPrompt,
    requestBrowserNotificationPermission,
    type BrowserPermission,
} from './browserNotifications';

type Props = {
    open: boolean;
    permission: BrowserPermission;
    onClose: () => void;
    onPermissionChange: (permission: BrowserPermission) => void;
};

/**
 * Overlay shown when Notification.permission is still "default" (or re-opened
 * after deny). Explains follow-up alerts, then triggers the browser permission
 * prompt from a user gesture (required by browsers).
 */
const PushPermissionOverlay = ({
    open,
    permission,
    onClose,
    onPermissionChange,
}: Props) => {
    const [requesting, setRequesting] = useState(false);
    const deniedHint = permission === 'denied';

    const handleEnable = async () => {
        setRequesting(true);
        try {
            const next = await requestBrowserNotificationPermission();
            onPermissionChange(next);
            if (next === 'granted') {
                onClose();
            }
        } finally {
            setRequesting(false);
        }
    };

    const handleNotNow = () => {
        dismissPushPermissionPrompt();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleNotNow}
            maxWidth="sm"
            fullWidth
            aria-labelledby="push-permission-title"
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(15, 23, 42, 0.72)',
                    },
                },
            }}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    px: { xs: 1, sm: 2 },
                    py: 1,
                },
            }}
        >
            <DialogTitle id="push-permission-title" sx={{ pb: 1 }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    {deniedHint ? (
                        <NotificationsOffIcon color="warning" fontSize="large" />
                    ) : (
                        <NotificationsActiveIcon color="primary" fontSize="large" />
                    )}
                    <span>
                        {deniedHint
                            ? 'Notifications are blocked'
                            : 'Enable follow-up notifications'}
                    </span>
                </Box>
            </DialogTitle>
            <DialogContent>
                {deniedHint ? (
                    <Typography variant="body1" color="text.secondary">
                        Your browser blocked notifications for this site. Open
                        the lock / site-settings icon in the address bar, set
                        Notifications to <strong>Allow</strong>, then reload
                        this page to receive estimate follow-up alerts.
                    </Typography>
                ) : (
                    <>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            Approve browser notifications so you get alerted when
                            a pending estimate needs a follow-up — even if this
                            tab is in the background.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Your browser will show a permission prompt next.
                            Choose <strong>Allow</strong> to receive those
                            alerts.
                        </Typography>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={handleNotNow} color="inherit" disabled={requesting}>
                    {deniedHint ? 'Close' : 'Not now'}
                </Button>
                {!deniedHint && (
                    <Button
                        onClick={handleEnable}
                        variant="contained"
                        color="primary"
                        disabled={requesting}
                        autoFocus
                    >
                        {requesting ? 'Waiting for browser…' : 'Allow notifications'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PushPermissionOverlay;
